import { FormEvent, useCallback, useEffect, useState } from 'react';
import AdvancedSearch from './components/AdvancedSearch';
import PaginationBar from './components/PaginationBar';
import PeopleCard from './components/PeopleCard';
import RecaptchaModal from './components/RecaptchaModal';
import type { DeptMap, PersonResult, SearchResponse } from './types';
import { listDeptsUrl } from './utils/endpoint';

type Props = {
  /** workfolio.php URL (relative or absolute) */
  endpoint: string;
};

type SearchParams = {
  page: number;
  pageSize: number;
  sort_by: string;
  token?: string | null;
  keyword?: string;
  department?: string;
  ucid?: string;
  location?: string;
  phone?: string;
};

export default function PeopleDirectory({ endpoint }: Props) {
  const qp = new URLSearchParams(window.location.search);
  const initialPage = Number(qp.get('page')) || 1;
  const initialKeyword = qp.get('keyword') ?? '';
  const initialDept = qp.get('department') ?? '';
  const initialUcid = qp.get('ucid') ?? '';
  const initialLocation = qp.get('location') ?? '';
  const initialPhone = qp.get('phone') ?? '';
  const initialSort = qp.get('sort_by') ?? 'last_name';

  const [page, setPage] = useState(initialPage);
  const [pageSize] = useState(10);
  const [totalPage, setTotalPage] = useState(0);
  const [totalResult, setTotalResult] = useState(0);
  const [data, setData] = useState<PersonResult[]>([]);
  const [keyword, setKeyword] = useState(initialKeyword);
  const [advancedOpen, setAdvancedOpen] = useState(
    Boolean(initialDept || initialUcid || initialPhone || initialLocation)
  );
  const [department, setDepartment] = useState(initialDept);
  const [ucid, setUcid] = useState(initialUcid);
  const [location, setLocation] = useState(initialLocation);
  const [phone, setPhone] = useState(initialPhone);
  const [dept, setDept] = useState<DeptMap>({});
  const [loading, setLoading] = useState(false);
  const [sortBy, setSortBy] = useState(initialSort);
  const [token, setToken] = useState<string | null>(null);
  const [captchaDisplay, setCaptchaDisplay] = useState(false);

  const replaceHistory = useCallback((params: SearchParams) => {
    const q = new URLSearchParams(window.location.search);
    const sync: Record<string, string | undefined> = {
      page: String(params.page),
      sort_by: params.sort_by,
      keyword: params.keyword,
      department: params.department,
      ucid: params.ucid,
      location: params.location,
      phone: params.phone,
    };
    Object.entries(sync).forEach(([key, val]) => {
      if (val) q.set(key, val);
      else if (q.has(key)) q.delete(key);
    });
    window.history.replaceState(null, '', q.toString() ? `?${q.toString()}` : window.location.pathname);
  }, []);

  const performSearch = useCallback(
    async (params: SearchParams) => {
      setLoading(true);
      const body: Record<string, unknown> = {
        page: params.page,
        pageSize: params.pageSize,
        sort_by: params.sort_by,
      };
      if (params.token != null && params.token !== '') body.token = params.token;
      if (params.keyword) body.keyword = params.keyword;
      if (params.department) body.department = params.department;
      if (params.ucid) body.ucid = params.ucid;
      if (params.location) body.location = params.location;
      if (params.phone) body.phone = params.phone;

      try {
        const res = await fetch(endpoint, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body),
        });
        const response: SearchResponse = await res.json();

        if (response.captcha_req) {
          setCaptchaDisplay(true);
          setLoading(false);
          return;
        }

        const results = response.results ?? [];
        const total = response.total ?? 0;
        setData(results);
        setTotalResult(total);
        setTotalPage(total > 0 ? Math.ceil(total / params.pageSize) : 0);
        setLoading(false);

        replaceHistory(params);
      } catch (e) {
        console.error(e);
        setLoading(false);
      }
    },
    [endpoint, replaceHistory]
  );

  const buildParams = useCallback(
    (overrides: Partial<SearchParams> = {}): SearchParams => ({
      page: overrides.page ?? page,
      pageSize: overrides.pageSize ?? pageSize,
      sort_by: overrides.sort_by ?? sortBy,
      token: overrides.token !== undefined ? overrides.token : token,
      keyword: overrides.keyword !== undefined ? overrides.keyword : keyword,
      department: overrides.department !== undefined ? overrides.department : department,
      ucid: overrides.ucid !== undefined ? overrides.ucid : ucid,
      location: overrides.location !== undefined ? overrides.location : location,
      phone: overrides.phone !== undefined ? overrides.phone : phone,
    }),
    [page, pageSize, sortBy, token, keyword, department, ucid, location, phone]
  );

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(listDeptsUrl(endpoint));
        const json: DeptMap = await res.json();
        if (!cancelled) setDept(json);
      } catch (e) {
        console.error(e);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [endpoint]);

  useEffect(() => {
    const hasAdvanced = Boolean(initialDept || initialUcid || initialPhone || initialLocation);
    if (initialKeyword || hasAdvanced) {
      void performSearch(
        buildParams({
          page: initialPage,
          keyword: initialKeyword,
          department: initialDept,
          ucid: initialUcid,
          location: initialLocation,
          phone: initialPhone,
          sort_by: initialSort,
        })
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- initial load only
  }, []);

  useEffect(() => {
    if (page >= 1 && totalResult > 0) {
      document.title = `Directory Search | Page ${page} | People`;
    } else {
      document.title = 'Directory Search | People';
    }
  }, [page, totalResult]);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const target = event.target;
    const { name, value: raw } = target;

    if (name === 'department') {
      const input = document.getElementById('department') as HTMLInputElement | null;
      const shownVal = input?.value ?? '';
      if (shownVal === "St. Michael's Med Ctr Health Svs" || shownVal === "Bursar's Office") {
        setDepartment(shownVal);
        return;
      }
      let resolved = raw;
      document.querySelectorAll<HTMLOptionElement>('#people-client-org-list option').forEach((opt) => {
        if (opt.value === shownVal && opt.dataset.value) {
          resolved = opt.dataset.value;
        }
      });
      setDepartment(resolved);
      return;
    }

    if (name === 'keyword') setKeyword(raw);
    if (name === 'ucid') setUcid(raw);
    if (name === 'location') setLocation(raw);
    if (name === 'phone') setPhone(raw);
  };

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault();
    setPage(1);
    void performSearch(buildParams({ page: 1 }));
  };

  const handlePageChange = (next: number) => {
    setPage(next);
    void performSearch(buildParams({ page: next }));
  };

  const sortByChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const v = event.target.value;
    setSortBy(v);
    void performSearch(buildParams({ sort_by: v, page }));
  };

  const setReCAPTCHAToken = (t: string) => {
    setToken(t);
    setCaptchaDisplay(false);
    void performSearch(buildParams({ token: t }));
  };

  const onReCAPTCHAClose = () => {
    setCaptchaDisplay(false);
    setLoading(false);
    setKeyword('');
    window.location.reload();
  };

  const resetFilters = () => {
    setKeyword('');
    setDepartment('');
    setUcid('');
    setLocation('');
    setPhone('');
    window.history.replaceState(null, '', window.location.pathname);
  };

  const resetDept = () => {
    setDepartment('');
  };

  const toggleAdvancedSearch = () => {
    setAdvancedOpen((o) => !o);
  };

  const advancedState = {
    advancedOpen,
    department,
    ucid,
    location,
    phone,
    dept,
  };

  const loadingBlock = (
    <div className="page-wrapper">
    <div className="people-client-loading">
      <div className="people-client-loading__dots" aria-hidden>
        <span />
        <span />
        <span />
      </div>
    </div>
    </div>
  );

  return (
    <>
      <form onSubmit={handleSubmit} className="people-client-search-form">
        <div className="people-client-search-bar-wrap">
          <label htmlFor="people-keyword" className="visually-hidden">
            Search for a person&apos;s name
          </label>
          <div className="degree-finder-search people-client-keyword">
            <input
              id="people-keyword"
              type="search"
              name="keyword"
              placeholder="Search for a person's name"
              onChange={handleChange}
              value={keyword}
              autoComplete="off"
            />
            <svg viewBox="0 0 30 30" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
              <path
                d="M13.0985 25.197C19.7803 25.197 25.197 19.7803 25.197 13.0985C25.197 6.41668 19.7803 1 13.0985 1C6.41668 1 1 6.41668 1 13.0985C1 19.7803 6.41668 25.197 13.0985 25.197Z"
                stroke="#CC0000"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M28.2231 28.2215L21.6445 21.6429"
                stroke="#CC0000"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </div>
        </div>
        <AdvancedSearch
          state={advancedState}
          toggleAdvancedSearch={toggleAdvancedSearch}
          handleChange={handleChange}
          resetFilters={resetFilters}
          resetDept={resetDept}
        />
      </form>

      <div className="new-peopleclient-wrapper">
        {!loading && totalResult > 0 ? (
          <div className="display-options people-client-display-options">
            <div className="results-info people-client-results-count">
              <span id="showing">
                Showing {(page - 1) * pageSize + 1} -{' '}
                {page * pageSize > totalResult ? totalResult : page * pageSize} of {totalResult} results
              </span>
            </div>
            <div className="results-sort">
              <span className="label" id="people-sort-label">
                Sort by
              </span>
              <select
                id="people-sort-by"
                name="sort_by"
                value={sortBy}
                onChange={sortByChange}
                aria-labelledby="people-sort-label"
              >
                <option value="last_name">Last name</option>
                <option value="first_name">First name</option>
              </select>
            </div>
          </div>
        ) : null}

        <RecaptchaModal open={captchaDisplay} onVerified={setReCAPTCHAToken} onClose={onReCAPTCHAClose} />

        <div className="paragraph paragraph--type--faculty horizontal">
          <div className="people-container">
            <ul className="people-list list-unstyled row gx-0 gx-md-10px gy-5 gy-md-60px mb-0 justify-content-around people-client-items">
              {loading ? (
                <li className="col-12">
                  {loadingBlock}
                </li>
              ) : (
                data.map((d, i) => <PeopleCard key={`${d.ucid}-${i}`} person={d} />)
              )}
            </ul>
          </div>
        </div>

        {!loading && totalResult > 0 ? (
          <PaginationBar page={page} totalPage={totalPage} onChange={handlePageChange} />
        ) : null}
      </div>
    </>
  );
}
